import { Injectable, OnDestroy, Signal, WritableSignal, computed, inject } from '@angular/core';
import { Aptitude, AptitudeTask, EvalueeAnswer, TaskResult, AptitudeScore } from '@career-scope/models';
import { EvalueeService } from './evaluee.service';
import { AssessmentService } from './assessment.service';
import { FeaturesSecurityService } from './features-security.service';
import { Subscription, tap } from 'rxjs';

@Injectable()
export abstract class BaseAptitudeService implements OnDestroy {
  protected evalueeService = inject(EvalueeService);
  protected assessmentService = inject(AssessmentService);
  protected featuresSecurityService = inject(FeaturesSecurityService);
  private subscription?: Subscription;

  abstract tasks: WritableSignal<AptitudeTask[]>;
  abstract aptitudes: WritableSignal<Aptitude[]>;
  totalTasks = computed(() => this.tasks().length);

  getTasks(): Signal<AptitudeTask[]> {
    return this.tasks;
  }

  markTaskComplete(taskId: number) {
    this.tasks.update((tasks: AptitudeTask[]) =>
      tasks.map((task: AptitudeTask) => task.id === taskId ? { ...task, complete: true } : task)
    );
  }

  updateQuizInProgress(quizInProgress: boolean) {
    this.assessmentService.quizInProgress.update(() => quizInProgress);
  }

  getCompletedTaskCount() {
    return this.tasks().filter(task => task.complete).length;
  }

  loadExerciseComplete(taskResults: TaskResult[]) {
    const completedExercises = taskResults.filter(result => result.completedDate);
    completedExercises.map(result => this.markTaskComplete(result.taskId));
  }

  resetAllExercisesAndAnswers() {
    this.tasks.update(tasks => tasks.map(task => ({ ...task, questions: task.questions.map(question => ({ ...question, answer: null, timeToComplete: 0 })), practiceQuestions: task.practiceQuestions.map(question => ({ ...question, answer: null, timeToComplete: 0 })), complete: false })));
  }

  resetOneExerciseAnswers(taskId: number) {
    this.tasks.update(tasks => tasks.map(task => task.id === taskId ? { ...task, questions: task.questions.map(question => ({ ...question, answer: null, timeToComplete: 0 })), practiceQuestions: task.practiceQuestions.map(question => ({ ...question, answer: null, timeToComplete: 0 })) } : task));
  }

  saveAptitudeTask(task: AptitudeTask, timeToComplete: number) {
    const assessment = this.assessmentService.assessment.value;

    if (!assessment) {
      return;
    }

    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;

    const taskResults = this.updateTaskResults(assessment.taskResults || [], task.id, timeToComplete, screenWidth, screenHeight);
    const evalueeAnswers = this.updateAptitudeAnswers(assessment.evalueeAnswers, task);

    const lastActivity = task.name + ' completed';
    const lastActivityDate = new Date(Date.now());

    this.markTaskComplete(task.id);

    const completedTaskCount = this.getCompletedTaskCount();
    const exerciseStatus = completedTaskCount + '/' + this.totalTasks() + ' tasks completed';
    const exercisesComplete = completedTaskCount === this.totalTasks() ? new Date(Date.now()) : null;
    const aptitudeScores: AptitudeScore[] = [];

    this.assessmentService.saveAssessment({ taskResults, evalueeAnswers, exercisesComplete, aptitudeScores }, assessment.portalId, assessment.evalueeId, assessment.id);
    this.evalueeService.updateEvaluee({ exercisesComplete, lastActivity, lastActivityDate, exerciseStatus });
  }

  updateExerciseTimeModifier() {
    this.subscription?.unsubscribe();
    this.subscription = this.featuresSecurityService.getExerciseTimeModifier().pipe(
      tap(modifier => {
        this.tasks.update(tasks => tasks.map(task => ({ ...task, timeOutLength: task.timeOutLength * (modifier || 1) })));
      })
    ).subscribe();
  }

  protected updateTaskResults(taskResults: TaskResult[], taskId: number, timeToComplete: number, screenWidth?: number, screenHeight?: number): TaskResult[] {
    const taskResult: TaskResult = {
      taskId,
      timeToComplete: Math.floor(timeToComplete),
      screenWidth: screenWidth || null,
      screenHeight: screenHeight || null,
      completedDate: new Date(Date.now()),
      language: 'English',
      audioEnabled: this.featuresSecurityService.getAudioEnabled().value,
      exerciseTimeModifier: this.getExerciseTimeModifierSaveValue(this.featuresSecurityService.getExerciseTimeModifier().value),
      correct: null,
      incorrect: null,
      unanswered: null
    };

    const taskResultIndex = taskResults.findIndex(result => result.taskId === taskId);

    if (taskResultIndex === -1) {
      taskResults.push(taskResult);
    } else {
      taskResults[taskResultIndex] = taskResult;
    }

    return taskResults;
  }

  protected getExerciseTimeModifierSaveValue(value: number) {
    if (!value || value === 1) {
      return null;
    }
    return value;
  }

  protected updateAptitudeAnswers(answers: EvalueeAnswer[], task: AptitudeTask): EvalueeAnswer[] {
    const filteredAnswers = answers.filter(answer => answer.taskId !== task.id);
    const aptitudeAnswers = this.getAnswersByAptitudeTask(task);

    return [...filteredAnswers, ...aptitudeAnswers];
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  //#region HQ Functions

  completeExerciseHQ(taskId: number) {
    const assessment = this.assessmentService.assessment.value;

    if (!assessment) {
      return;
    }

    const taskAnswers = this.sampleAnswers.filter(answer => answer.task === taskId);

    this.tasks.update(tasks => {
      return tasks.map(task => {
        return task.id === taskId ? ({
          ...task, complete: true, questions: task.questions.map(question => {
            return ({ ...question, answer: taskAnswers.find(answer => answer.qNum === question.id)?.answer || null, completedAt: new Date() });
          })
        }) : task;
      });
    });

    this.markTaskComplete(taskId);

    const task = this.tasks().find(task => task.id === taskId);

    if (!task) {
      return;
    }

    this.saveAptitudeTask(task, 0);
  }

  completeExercisesHQ() {
    const assessment = this.assessmentService.assessment.value;

    if (!assessment) {
      return;
    }

    this.tasks.update(tasks => {
      return tasks.map(task => {
        return ({
          ...task,
          complete: true,
          questions: task.questions.map(question => {
            const answer = this.sampleAnswers.find(a => a.qNum === question.id);
            return ({ ...question, answer: answer ? answer.answer : null, completedAt: new Date() });
          })
        });
      });
    });

    let evalueeAnswers = assessment.evalueeAnswers;
    let taskResults = assessment.taskResults || [];

    this.getTasks()().forEach(task => {
      const filteredAnswers = evalueeAnswers.filter(answer => answer.taskId !== task.id);
      const answers = this.getAnswersByAptitudeTask(task);
      const result = this.updateTaskResults(taskResults, task.id, task.timeOutLength);

      evalueeAnswers = [...filteredAnswers, ...answers];
      taskResults = [...result];
      this.markTaskComplete(task.id);
    });

    const exercisesComplete = new Date(Date.now());
    const lastActivity = 'Exercises Completed';
    const lastActivityDate = new Date(Date.now());
    const exerciseStatus = '7/7 tasks completed';

    this.assessmentService.saveAssessment({ evalueeAnswers, taskResults, exercisesComplete }, assessment.portalId, assessment.evalueeId, assessment.id);
    this.evalueeService.updateEvaluee({ exercisesComplete, lastActivity, lastActivityDate, exerciseStatus });
  }

  resetExercisesHQ() {
    if (!this.featuresSecurityService.getSuperUser().value) {
      return;
    }

    const assessment = this.assessmentService.assessment.value;

    if (!assessment) {
      return;
    }

    this.resetAllExercisesAndAnswers();

    const taskIds = this.tasks().map(task => task.id);

    const evalueeAnswers = assessment.evalueeAnswers.filter(answer => !taskIds.includes(answer.taskId));

    this.assessmentService.saveAssessment({ evalueeAnswers, taskResults: [], exercisesComplete: null, aptitudeScores: [], completedDate: null, lastModifiedDate: new Date(Date.now()) }, assessment.portalId, assessment.evalueeId, assessment.id);
    this.evalueeService.updateEvaluee({ exercisesComplete: null, lastActivity: 'Exercises Reset', lastActivityDate: new Date(Date.now()), exerciseStatus: 'Exercises Reset', status: 'in progress' });
  }

  getAnswersByAptitudeTask(task: AptitudeTask): EvalueeAnswer[] {
    return task.questions
      .filter(question => !!question.answer)
      .map(question => ({
        taskId: task.id,
        questionId: question.id,
        answer: question.answer as number,
        completedAt: question.completedAt || null,
      })) || [];
  }

  sampleAnswers = [{ task: 1, qNum: 30, answer: 3 }, { task: 1, qNum: 31, answer: 3 }, { task: 1, qNum: 32, answer: 4 }, { task: 1, qNum: 33, answer: 1 }, { task: 1, qNum: 34, answer: 2 }, { task: 1, qNum: 35, answer: 3 }, { task: 1, qNum: 36, answer: 4 }, { task: 1, qNum: 37, answer: 3 }, { task: 1, qNum: 38, answer: 2 }, { task: 1, qNum: 39, answer: 2 }, { task: 1, qNum: 40, answer: 3 }, { task: 1, qNum: 41, answer: 3 }, { task: 1, qNum: 42, answer: 2 }, { task: 1, qNum: 43, answer: 1 }, { task: 1, qNum: 44, answer: 4 }, { task: 1, qNum: 45, answer: 3 }, { task: 1, qNum: 46, answer: 4 }, { task: 1, qNum: 47, answer: 0 }, { task: 1, qNum: 48, answer: 0 }, { task: 1, qNum: 49, answer: 0 }, { task: 1, qNum: 50, answer: 0 }, { task: 1, qNum: 51, answer: 0 }, { task: 1, qNum: 52, answer: 0 }, { task: 1, qNum: 53, answer: 0 }, { task: 1, qNum: 54, answer: 0 }, { task: 1, qNum: 55, answer: 0 }, { task: 1, qNum: 56, answer: 0 }, { task: 1, qNum: 57, answer: 0 }, { task: 1, qNum: 58, answer: 0 }, { task: 1, qNum: 59, answer: 0 }, { task: 3, qNum: 62, answer: 3 }, { task: 3, qNum: 63, answer: 2 }, { task: 3, qNum: 64, answer: 2 }, { task: 3, qNum: 65, answer: 3 }, { task: 3, qNum: 66, answer: 2 }, { task: 3, qNum: 67, answer: 3 }, { task: 3, qNum: 68, answer: 3 }, { task: 3, qNum: 69, answer: 3 }, { task: 3, qNum: 70, answer: 3 }, { task: 3, qNum: 71, answer: 3 }, { task: 3, qNum: 72, answer: 1 }, { task: 3, qNum: 73, answer: 4 }, { task: 3, qNum: 74, answer: 2 }, { task: 3, qNum: 75, answer: 3 }, { task: 3, qNum: 76, answer: 1 }, { task: 3, qNum: 77, answer: 1 }, { task: 3, qNum: 78, answer: 3 }, { task: 3, qNum: 79, answer: 3 }, { task: 3, qNum: 80, answer: 3 }, { task: 3, qNum: 81, answer: 2 }, { task: 3, qNum: 82, answer: 2 }, { task: 3, qNum: 83, answer: 2 }, { task: 3, qNum: 84, answer: 3 }, { task: 3, qNum: 85, answer: 1 }, { task: 3, qNum: 86, answer: 4 }, { task: 3, qNum: 87, answer: 2 }, { task: 3, qNum: 88, answer: 4 }, { task: 3, qNum: 89, answer: 4 }, { task: 3, qNum: 90, answer: 1 }, { task: 3, qNum: 91, answer: 4 }, { task: 6, qNum: 99, answer: 1 }, { task: 6, qNum: 100, answer: 1 }, { task: 6, qNum: 101, answer: 2 }, { task: 6, qNum: 102, answer: 2 }, { task: 6, qNum: 103, answer: 1 }, { task: 6, qNum: 104, answer: 1 }, { task: 6, qNum: 105, answer: 2 }, { task: 6, qNum: 106, answer: 2 }, { task: 6, qNum: 107, answer: 1 }, { task: 6, qNum: 108, answer: 1 }, { task: 6, qNum: 109, answer: 1 }, { task: 6, qNum: 110, answer: 1 }, { task: 6, qNum: 111, answer: 2 }, { task: 6, qNum: 112, answer: 2 }, { task: 6, qNum: 113, answer: 2 }, { task: 6, qNum: 114, answer: 1 }, { task: 6, qNum: 115, answer: 1 }, { task: 6, qNum: 116, answer: 0 }, { task: 6, qNum: 117, answer: 0 }, { task: 6, qNum: 118, answer: 0 }, { task: 6, qNum: 119, answer: 0 }, { task: 6, qNum: 120, answer: 0 }, { task: 6, qNum: 121, answer: 0 }, { task: 6, qNum: 122, answer: 0 }, { task: 6, qNum: 123, answer: 0 }, { task: 6, qNum: 124, answer: 0 }, { task: 6, qNum: 125, answer: 0 }, { task: 6, qNum: 126, answer: 0 }, { task: 6, qNum: 127, answer: 0 }, { task: 6, qNum: 128, answer: 0 }, { task: 8, qNum: 131, answer: 1 }, { task: 8, qNum: 132, answer: 4 }, { task: 8, qNum: 133, answer: 2 }, { task: 8, qNum: 149, answer: 1 }, { task: 8, qNum: 150, answer: 1 }, { task: 8, qNum: 151, answer: 2 }, { task: 8, qNum: 152, answer: 4 }, { task: 8, qNum: 153, answer: 4 }, { task: 8, qNum: 154, answer: 2 }, { task: 8, qNum: 155, answer: 2 }, { task: 8, qNum: 156, answer: 1 }, { task: 10, qNum: 163, answer: 1 }, { task: 10, qNum: 164, answer: 2 }, { task: 10, qNum: 165, answer: 1 }, { task: 10, qNum: 166, answer: 2 }, { task: 10, qNum: 167, answer: 4 }, { task: 10, qNum: 168, answer: 2 }, { task: 10, qNum: 169, answer: 1 }, { task: 10, qNum: 170, answer: 2 }, { task: 10, qNum: 171, answer: 2 }, { task: 10, qNum: 172, answer: 4 }, { task: 10, qNum: 173, answer: 3 }, { task: 10, qNum: 174, answer: 1 }, { task: 10, qNum: 175, answer: 3 }, { task: 10, qNum: 176, answer: 2 }, { task: 10, qNum: 177, answer: 1 }, { task: 10, qNum: 178, answer: 2 }, { task: 10, qNum: 179, answer: 3 }, { task: 10, qNum: 180, answer: 2 }, { task: 10, qNum: 181, answer: 3 }, { task: 10, qNum: 182, answer: 2 }, { task: 10, qNum: 183, answer: 1 }, { task: 10, qNum: 184, answer: 3 }, { task: 10, qNum: 185, answer: 1 }, { task: 10, qNum: 186, answer: 2 }, { task: 10, qNum: 187, answer: 3 }, { task: 10, qNum: 188, answer: 1 }, { task: 10, qNum: 189, answer: 2 }, { task: 10, qNum: 190, answer: 3 }, { task: 10, qNum: 191, answer: 1 }, { task: 10, qNum: 192, answer: 2 }, { task: 12, qNum: 195, answer: 2 }, { task: 12, qNum: 196, answer: 1 }, { task: 12, qNum: 197, answer: 2 }, { task: 12, qNum: 198, answer: 1 }, { task: 12, qNum: 199, answer: 3 }, { task: 12, qNum: 200, answer: 3 }, { task: 12, qNum: 201, answer: 2 }, { task: 12, qNum: 202, answer: 4 }, { task: 12, qNum: 203, answer: 3 }, { task: 12, qNum: 204, answer: 2 }, { task: 12, qNum: 205, answer: 3 }, { task: 12, qNum: 206, answer: 1 }, { task: 12, qNum: 207, answer: 4 }, { task: 12, qNum: 208, answer: 2 }, { task: 12, qNum: 209, answer: 1 }, { task: 12, qNum: 210, answer: 1 }, { task: 12, qNum: 211, answer: 1 }, { task: 12, qNum: 212, answer: 2 }, { task: 12, qNum: 213, answer: 4 }, { task: 12, qNum: 214, answer: 1 }, { task: 12, qNum: 215, answer: 3 }, { task: 12, qNum: 216, answer: 1 }, { task: 12, qNum: 217, answer: 3 }, { task: 14, qNum: 220, answer: 1 }, { task: 14, qNum: 221, answer: 3 }, { task: 14, qNum: 222, answer: 2 }, { task: 14, qNum: 223, answer: 2 }, { task: 14, qNum: 224, answer: 1 }, { task: 14, qNum: 225, answer: 1 }, { task: 14, qNum: 226, answer: 2 }, { task: 14, qNum: 227, answer: 4 }, { task: 14, qNum: 228, answer: 4 }, { task: 14, qNum: 229, answer: 1 }, { task: 14, qNum: 230, answer: 3 }, { task: 14, qNum: 231, answer: 1 }, { task: 14, qNum: 232, answer: 2 }, { task: 14, qNum: 233, answer: 3 }, { task: 14, qNum: 234, answer: 3 }, { task: 14, qNum: 235, answer: 4 }, { task: 14, qNum: 236, answer: 1 }, { task: 14, qNum: 237, answer: 2 }, { task: 14, qNum: 238, answer: 3 }, { task: 14, qNum: 239, answer: 2 }, { task: 14, qNum: 240, answer: 2 }, { task: 14, qNum: 241, answer: 3 }, { task: 14, qNum: 242, answer: 2 }, { task: 14, qNum: 243, answer: 2 }, { task: 14, qNum: 244, answer: 2 }, { task: 14, qNum: 245, answer: 1 }, { task: 14, qNum: 246, answer: 4 }, { task: 14, qNum: 247, answer: 3 }, { task: 14, qNum: 248, answer: 3 }, { task: 14, qNum: 249, answer: 3 }];
  //#endregion
}

//#region 